<template>
  <v-row justify="center">
    <v-dialog
      :value="value"
      hide-overlay
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar
          flat
          height="80px"
          color="accent"
        >
          <v-toolbar-title class="event-detail-title">
            Event Detail
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            icon
            @click="$emit('input')"
          >
            <v-icon
              size="20"
              color="white"
            >
              $faTimes
            </v-icon>
          </v-btn>
        </v-toolbar>
        <div
          ref="pdfExport"
          class="event-detail-card"
        >
          <div
            data-html2canvas-ignore="true"
            class="event-detail-header"
          >
            <div class="event-detail-label">
              <div class="event-detail-label__icon">
                <v-icon
                  size="20"
                  color="white"
                >
                  {{ eventLabel.icon }}
                </v-icon>
              </div>
              <div class="event-detail-label__type">
                {{ eventLabel.label }}
              </div>
            </div>
            <v-btn
              class="icon-btn"
              elevation="1"
              color="primary"
              @click="exportPDF"
            >
              <font-awesome-icon
                :icon="['fas', 'file-export']"
              />
              Export PDF
            </v-btn>
          </div>
          <div class="event-detail-description">
            <v-container class="event-detail-table">
              <v-row>
                <v-col cols="3">
                  <v-subheader class="event-detail-table__header">
                    Organization
                  </v-subheader>
                  <span class="event-detail-table__data">
                    {{ eventOrganization }}
                  </span>
                </v-col>
                <v-spacer />
                <v-col cols="5">
                  <v-subheader class="event-detail-table__header">
                    Start Time
                  </v-subheader>
                  <span class="event-detail-table__data">
                    {{ eventStartDateTime | formatDateForTable }}
                  </span>
                </v-col>
                <v-spacer />
                <v-col cols="3">
                  <v-subheader class="event-detail-table__header">
                    Activity Sensor
                  </v-subheader>
                  <span class="event-detail-table__data">
                    {{ eventActivityZone }}
                  </span>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="3">
                  <v-subheader class="event-detail-table__header">
                    Property Name
                  </v-subheader>
                  <span class="event-detail-table__data">
                    {{ eventPropertyName }}
                  </span>
                  <div class="event-detail-table__detail">
                    {{ eventPropertyAddress }}
                  </div>
                </v-col>
                <v-spacer />
                <v-col cols="5">
                  <v-subheader class="event-detail-table__header">
                    End Time
                  </v-subheader>
                  <span class="event-detail-table__data">
                    {{ eventEndDateTimeOrOpen }}
                  </span>
                </v-col>
                <v-spacer />
                <v-col cols="3">
                  <v-subheader class="event-detail-table__header">
                    Duration
                  </v-subheader>
                  <span class="event-detail-table__data">
                    {{ eventDuration }}
                  </span>
                </v-col>
              </v-row>
            </v-container>
          </div>
          <v-divider />
          <div style="max-height: 350px">
            <v-row class="ml-3">
              <zones-legend
                :zones-legend="zonesLegend"
                @toggle-zone="toggleZoneActive"
              />
            </v-row>
            <v-row>
              <v-spacer />
              <div
                data-html2canvas-ignore="true"
                class="d-flex align-center mb-4"
              />
            </v-row>
          </div>
          <valkyrie-graph-legend ref="graphLegend" />
          <v-skeleton-loader
            v-if="!resolved"
            class="graph-loader mb-3"
            max-width="100%"
            height="300px"
            min-height="300px"
            type="image"
            loading
          />
          <graph
            v-else-if="resolved"
            :chart-model="chartModel"
            :chart-options="chartOptions"
            :x-axis-labels="xAxis"
            :render-key="rerenderKey"
            :timezone="eventTimeZone"
            :tooltip-points="graphTooltipData"
            height="300px"
            chart-name="eventDetailChart"
            class="mb-3"
            chart-type="event"
          />
          <div v-if="showOccupancy">
            <occupancy-graph-header />
            <v-skeleton-loader
              v-if="!resolved"
              class="graph-loader"
              max-width="100%"
              height="100px"
              min-height="100px"
              type="image"
              loading
            />
            <occupancy-graph
              v-else
              height="100px"
              :max="maxOccupancyGraph"
              :timezone="eventTimeZone"
              :chart-model="occChartModel"
              :render-key="rerenderKey"
              :activity-zones="occupancyDataByAz.map(az => az.azName)"
            />
          </div>
          <v-row
            justify="space-between"
            dense
          >
            <v-col class="ml-14">
              <span class="lower-date">{{ startDate.date | formatAxesLimit }}</span>
            </v-col>
            <v-col class="text-right mr-9">
              <span class="lower-date">{{ endDate.date | formatAxesLimit }}</span>
            </v-col>
          </v-row>
          <div
            data-html2canvas-ignore="true"
            class="event-detail-graph__controls"
          >
            <v-menu
              ref="startMenu"
              v-model="startMenu"
              :close-on-content-click="false"
              top
              nudge-top="50px"
              right
              @click.stop.prevent
            >
              <template #activator="{ on, attrs }">
                <v-btn
                  class="icon-btn"
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ startDate | formatDatePickerLabel }}
                  <v-icon
                    size="12"
                    color="white"
                    class="ml-3"
                  >
                    $faChevronDown
                  </v-icon>
                </v-btn>
              </template>
              <valkyrie-date-picker
                :initial-value="startDate"
                :allowed-dates="getAllowedStartDates"
                @cancel="startMenu = false"
                @submit-date="(date) => selectDate('start', date)"
              />
            </v-menu>
            <v-menu
              ref="endMenu"
              v-model="endMenu"
              :close-on-content-click="false"
              top
              nudge-top="50px"
              left
              @click.stop.prevent
            >
              <template #activator="{on, attrs}">
                <v-btn
                  class="icon-btn"
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  {{ endDate | formatDatePickerLabel }}
                  <v-icon
                    size="12"
                    color="white"
                    class="ml-3"
                  >
                    $faChevronDown
                  </v-icon>
                </v-btn>
              </template>
              <valkyrie-date-picker
                :initial-value="endDate"
                :allowed-dates="getAllowedEndDates"
                @cancel="endMenu = false"
                @submit-date="(date) => selectDate('end', date)"
              />
            </v-menu>
          </div>

          <div
            data-html2canvas-ignore="true"
            class="d-flex"
          >
            <div
              v-if="paginationEnabled"
              class="event-detail-pagination"
            >
              <div class="ma-auto">
                <v-btn
                  icon
                  class="my-auto"
                  @click="handlePageUpdate('left')"
                >
                  <v-icon size="12">
                    $faChevronLeft
                  </v-icon>
                </v-btn>
                <span class="my-auto">
                  {{ paginationText }}
                </span>
                <v-btn
                  icon
                  class="my-auto"
                  @click="handlePageUpdate('right')"
                >
                  <v-icon size="12">
                    $faChevronRight
                  </v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
  import axios from 'axios'
  import nonReactiveMixin from '@/helpers/non-reactive-mixin.js'
  import exportPdfMixin from '@/helpers/export-pdf-mixin.js'
  import occupancyGraphMixin from '@/helpers/occupancy-graph-mixin.js'
  import { OCCUPANCY } from '../../helpers/feature-constants'
  import { mapGetters } from 'vuex'
  import { getEventIcon, getEventLabel } from '@/helpers/event-helpers'
  import { DateTime } from 'luxon'
  import ValkyrieDatePicker from '@/components/base/ValkyrieDatePicker.vue'
  import OccupancyGraph from '../OccupancyGraph.vue'
  import Graph, { defaultGraphOptions } from '@/components/base/Graph.vue'
  import ValkyrieGraphLegend from '../base/ValkyrieGraphLegend.vue'
  import ZonesLegend from '../ZonesLegend.vue'
  import { nearAlert } from '@/components/base/GraphEventTooltip.js'
  import { faCircle, faComment } from '@/assets/icons/free-solid.js'
  import * as alertTypes from '@/helpers/alert-types'
  import OccupancyGraphHeader from '../OccupancyGraphHeader.vue'

  const colors = [
    '#25954A',
    '#CC6677',
    '#332288',
    '#0072B2',
    '#44AA99',
    '#882255',
    '#D55E00',
    '#E19B47',
  ]

  const minutes = 60 * 1000
  const hours = 60 * minutes
  const days = 24 * hours

  const axesLimitsString = 'LLL d, yyyy'
  const datePickerString = 'yyyy-LL-dd'
  const datePickerLabelString = 'dd LLL yyyy - h:mm a (ZZZZ)'
  const eventDetailTableString = "ccc, dd LLL yyyy 'at'  h:mm a (ZZZZ)"

  const convert12To24Hour = (hour, period) => {
    if (period === 'PM') {
      return hour !== 12 ? hour + 12 : hour
    } else {
      return hour !== 12 ? hour : hour - 12
    }
  }

  export default {
    name: 'EventDetailModal',
    components: {
      ValkyrieDatePicker,
      Graph,
      OccupancyGraph,
      ValkyrieGraphLegend,
      ZonesLegend,
      OccupancyGraphHeader,
    },
    filters: {
      formatAxesLimit (date) {
        return DateTime.fromFormat(date, datePickerString).toFormat(axesLimitsString)
      },
      formatDatePickerLabel (dateModel) {
        const { date, minute, period, timezone } = dateModel
        const hour = convert12To24Hour(dateModel.hour, period)
        const dateTime = DateTime.fromFormat(date, datePickerString).set({ hour, minute, second: 0 }).setZone(timezone)
        return dateTime.toFormat(datePickerLabelString)
      },
      formatDateForTable (value) {
        return value.toFormat(eventDetailTableString)
      },
    },
    mixins: [nonReactiveMixin, exportPdfMixin, occupancyGraphMixin],
    props: {
      filters: Array,
      value: {
        type: Boolean,
        default: false,
      },
      paginationEnabled: Boolean,
    },
    data () {
      return {
        resolved: false,
        xAxis: 'minute',
        zones: [],
        activeZones: {},
        rerenderKey: 0,
        graphData: {
          xValue: [],
          series: [],
        },
        startDate: {
          date: '1970-01-01',
          hour: '12',
          minute: '00',
          period: 'AM',
          timezone: '',
        },
        endDate: {
          date: '1970-01-01',
          hour: '12',
          minute: '00',
          period: 'PM',
          timezone: '',
        },
        startMenu: false,
        endMenu: false,
        showOccupancy: false,
      }
    },
    computed: {
      ...mapGetters({
        eventData: 'Events/selectedEvent',
        totalEvents: 'Events/totalEvents',
        eventsListPagination: 'Events/eventsListPagination',
        selectedEventIndex: 'Events/selectedEventIndex',
        isFeatureEligible: 'Organizations/getOrgFeatureEligibility',
      }),
      eventLabel () {
        const formattedLabel = this.eventData ? getEventLabel(this.eventData.eventClass) : ''
        return { icon: getEventIcon(this.eventData.eventClass), label: `${formattedLabel} Event` }
      },
      eventOrganization () {
        return this.eventData?.property?.organization?.name ?? ''
      },
      eventPropertyName () {
        return this.eventData?.property?.name ?? ''
      },
      propertyId () {
        return this.eventData?.property?.id ?? ''
      },
      eventPropertyAddress () {
        return ''
      },
      eventTimeZone () {
        return this.eventData?.property?.timezone ?? ''
      },
      eventStartDateTime () {
        return this.APIStringToDateTime(this.eventData.utcStartDateTime, this.eventTimeZone)
      },
      eventEndDateTimeOrOpen () {
        if (!this.eventData.utcEndDateTime) return 'Open'
        return this.eventEndDateTime.toFormat(eventDetailTableString)
      },
      eventEndDateTime () {
        const endDateTime = this.eventData.utcEndDateTime ? this.APIStringToDateTime(this.eventData.utcEndDateTime) : DateTime.utc()
        return endDateTime.setZone(this.eventTimeZone)
      },
      eventActivityZone () {
        return this.eventData?.activityZone?.activityZoneName ?? '--'
      },
      zoneColors () {
        return this.zones.reduce((acc, curr, i) => ({ [curr.id]: colors[i % colors.length], ...acc }), {})
      },
      zonesLegend () {
        return (this.zones || []).map((zone) => {
          return { ...zone, active: this.activeZones[zone.id], color: this.zoneColors[zone.id] }
        })
      },
      chartModel () {
        const chartModel = {
          labels: this.graphData.xValue,
          datasets: this.graphData.series?.map((set) => {
            if (set.type === 'nrs') return this.getNRSStyleProperties(set)
            else if (set.type === 'threshold') return this.getThresholdStyleProperties(set)
          }),
        }

        if (this.graphAlerts?.length) {
          chartModel.datasets.push(this.getAlertDataset(this.graphData.xValue, this.graphAlerts))
        }

        return chartModel
      },
      chartOptions () {
        return {
          ...defaultGraphOptions,
          annotation: {
            events: ['mouseenter', 'mouseleave'],
            annotations: [this.graphEventAnnotation],
          },
        }
      },
      eventDuration () {
        const { duration: durationInSeconds, utcEndDateTime } = this.eventData
        const duration = durationInSeconds * 1000
        if (!utcEndDateTime || !duration) return 'In Progress'
        const wholeDays = Math.floor(duration / days)
        const wholeHours = Math.floor((duration - wholeDays * days) / hours)
        const wholeMinutes = Math.floor((duration - wholeDays * days - wholeHours * hours) / minutes)
        let durationText = ''
        if (wholeDays !== 0) durationText += `${wholeDays}d` + ' '
        if (wholeHours !== 0) durationText += `${wholeHours}h` + ' '
        if (wholeMinutes !== 0) durationText += `${wholeMinutes}m`
        return durationText.trim()
      },
      paginationText () {
        return `${this.selectedEventIndex} of ${this.totalEvents} ${this.totalEvents > 1 ? 'events' : 'event'}`
      },
      graphEventAnnotation () {
        return {
          drawTime: 'beforeDatasetsDraw',
          type: 'box',
          id: 'box',
          xScaleID: 'x-axis-0',
          yScaleID: 'y-axis-0',
          xMin: DateTime.fromISO(this.eventData.localStartDateTime, { setZone: true }).set({ seconds: 0 }).toISO({ suppressMilliseconds: true }),
          xMax: this.eventData.localEndDateTime ? DateTime.fromISO(this.eventData.localEndDateTime, { setZone: true }).set({ seconds: 0 }).toISO({ suppressMilliseconds: true }) : null,
          yMin: 0,
          yMax: 100,
          borderColor: '#00000000',
          backgroundColor: '#50847B33',
          borderWidth: 0.0,
        }
      },
      graphTooltipData () {
        return {
          events: [],
          alerts: this.graphAlerts,
        }
      },
      iconSVG () {
        const svgString = `
        <svg 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 512 512" 
            height="23px"
            width="23px"
          >
            <path fill="black" d="${faCircle.icon[4]}"></path>
            <g transform="scale(0.58, 0.58) translate(172, 172)">
              <path fill="white" d="${faComment.icon[4]}"></path>
            </g>
        </svg>
        `
        const img = new Image(23, 23)
        img.src = `data:image/svg+xml;utf8,${svgString}`
        return img
      },
    },
    watch: {
      eventData (newData) {
        if (newData) {
          this.initializePageNum()
          if (this.propertyId) {
            this.setShowOccupancy()
            this.fetchAndInitializeActiveZones()
            const initialWindow = {
              startDateTime: this.eventStartDateTime.minus({ minutes: 30 }),
              endDateTime: this.eventEndDateTime.plus({ minutes: 30 }),
            }
            // make sure we don't fetch more than one week's worth of data
            if (initialWindow.endDateTime.diff(initialWindow.startDateTime, 'days').toObject().days > 7) {
              initialWindow.endDateTime = initialWindow.startDateTime.plus({ days: 7 })
            }
            this.initializeDatePickers(initialWindow)
            this.fetchAndRenderGraph(initialWindow)
          } else {
            this.showOccupancy = false
          }
        }
      },
    },
    methods: {
      setShowOccupancy () {
        this.showOccupancy = this.isFeatureEligible(OCCUPANCY).isEligible
      },
      initializeDatePickers (initialWindow) {
        this.startDate = {
          ...this.DateTimeToDatePickerModel(initialWindow.startDateTime),
          timezone: this.eventData?.property?.timezone ?? '',
        }
        this.endDate = {
          ...this.DateTimeToDatePickerModel(initialWindow.endDateTime),
          timezone: this.eventData?.property?.timezone ?? '',
        }
      },
      initializePageNum () {
        this.pageNum = this.initialPageNum
      },
      initializeActiveZones () {
        this.activeZones = (this.zones || []).reduce((acc, curr) => ({ [curr.id]: true, ...acc }), {})
      },
      APIStringToDateTime (ISOString, timezone) {
        return DateTime.fromISO(ISOString).setZone(timezone)
      },
      DateTimeToAPIString (dateTime) {
        return dateTime.toUTC().toISO()
      },
      DateTimeToDatePickerModel (dateTime) {
        return {
          date: dateTime.toFormat(datePickerString),
          hour: dateTime.hour,
          minute: dateTime.minute,
          period: dateTime.toFormat('a'),
        }
      },
      datePickerModelToDateTime (dateModel) {
        const { date, minute, period, timezone } = dateModel
        const hour = convert12To24Hour(dateModel.hour, period)
        return DateTime.fromFormat(date, datePickerString).set({ hour, minute, second: 0 }).setZone(timezone)
      },
      async fetchAndRenderGraph ({ startDateTime, endDateTime }) {
        this.resolved = false
        await this.fetchAlertAnnotations(this.DateTimeToAPIString(startDateTime), this.DateTimeToAPIString(endDateTime))
        return this.getGraphData({ startISO: this.DateTimeToAPIString(startDateTime), endISO: this.DateTimeToAPIString(endDateTime) })
          .then(({ nrs, occupancy }) => {
            this.markDataNonReactive(nrs)
            this.graphData = nrs
            if (occupancy) this.occupancyGraphData = occupancy
            this.resolved = true
          })
          .then(() => this.rerenderGraph())
      },
      async getGraphData ({ startISO, endISO }) {
        const response = await axios.get(`${this.$store.state.baseURL}/properties/graph/${this.propertyId}/${startISO}/${endISO}`, {})
        const dataObj = { nrs: response.data.data }

        if (this.showOccupancy) {
          try {
            const occupancyResponse = await this.getOccupancyGraphData(startISO, endISO)
            dataObj.occupancy = occupancyResponse.data.data
          } catch {
            dataObj.occupancy = {}
          }
        }
        return dataObj
      },
      async fetchAndInitializeActiveZones () {
        return axios.get(`${this.$store.state.baseURL}/properties/${this.propertyId}/activityzones`, {})
          .then((res) => { this.zones = res.data.data })
          .then(() => this.initializeActiveZones())
      },
      async fetchAlertAnnotations (startTime, endTime) {
        const payload =
          {
            filter: [
              { key: 'created_at', operator: '>=', value: startTime },
              { key: 'created_at', operator: '<=', value: endTime },
              { key: 'alert_type', operator: 'in', value: Object.values(alertTypes).join(',') },
              { key: 'property_id', operator: '=', value: `${this.propertyId}` },
            ],
            pagination: { perPage: 999 },
          }
        return axios.post(`${this.$store.state.baseURL}/alerts/list`, payload)
          .then((res) => {
            this.graphAlerts = (res.data.data || []).map(alertItem => {
              return {
                id: alertItem.id,
                tooltipType: 'alert',
                type: alertItem.alert_type,
                timestamp: alertItem.created_at,
                activityZone: alertItem.activity_zone_id,
              }
            })
          })
      },
      rerenderGraph () {
        this.rerenderKey++
      },
      getThresholdStyleProperties (dataset) {
        return {
          id: dataset.zoneId,
          label: dataset.type,
          borderColor: this.zoneColors[dataset.zoneId],
          hidden: !this.activeZones[dataset.zoneId],
          data: dataset.yValue,
          fill: false,
          lineTension: 0,
          borderWidth: 3,
          borderDash: [8, 4],
          pointRadius: 0,
          pointHitRadius: 0,
          pointHoverRadius: 0,
        }
      },
      getNRSStyleProperties (dataset) {
        return {
          id: dataset.zoneId,
          label: dataset.type,
          borderColor: this.zoneColors[dataset.zoneId],
          hidden: !this.activeZones[dataset.zoneId],
          data: dataset.yValue.map(nrs => nrs === -1 ? null : nrs),
          fill: false,
          lineTension: 0,
          borderWidth: 2,
          cubicInterpolationMode: 'monotone',
          pointRadius: 0,
          pointHitRadius: 0,
          pointHoverRadius: 0,
        }
      },
      getAlertDataset (xValues, alertPoints) {
        const unmarkedAlertPoints = JSON.parse(JSON.stringify(alertPoints)) // copy w/o reactivity

        const alertData = xValues.map(timestamp => {
          const utcTimestamp = DateTime.fromISO(timestamp).toUTC().toISO()
          const utcTime = new Date(utcTimestamp)

          if ((utcTime < this.eventStartDateTime) || (utcTime > this.eventEndDateTime)) {
            return
          }

          for (let i = unmarkedAlertPoints.length - 1; i >= 0; i--) {
            const alert = unmarkedAlertPoints[i]

            if (nearAlert(utcTimestamp, alert.timestamp, 1)) {
              unmarkedAlertPoints.splice(i, 1)
              return 3
            }
          }
        })

        return {
          data: alertData,
          pointStyle: this.iconSVG,
          pointHitRadius: 5,
        }
      },
      toggleZoneActive (toggledZoneId) {
        this.activeZones[toggledZoneId] = !this.activeZones[toggledZoneId]
        this.rerenderGraph()
      },
      handlePageUpdate (direction) {
        const delta = { left: -1, right: 1 }[direction]
        const clamp = (value) => Math.max(Math.min(value, this.totalEvents), 1)
        this.$store.commit('Events/updateSelectedEventIndex', clamp(this.selectedEventIndex + delta))
        const payload = {
          pagination: {
            perPage: 1,
            pageNum: this.selectedEventIndex,
          },
          filter: [
            { key: 'event_type', operator: '=', value: 'EXTERNAL' },
            ...(this.filters ?? []),
          ],
        }
        this.$store.dispatch('Events/fetchSelectedEvent', payload)
      },
      selectDate (endpoint, date) {
        const { hour: hourStr, minute: minuteStr, ...rest } = date
        const hour = parseInt(hourStr, 10)
        const minute = parseInt(minuteStr, 10)
        if (endpoint === 'start') this.startDate = { hour, minute, ...rest }
        if (endpoint === 'end') this.endDate = { hour, minute, ...rest }
        const startDateTime = this.datePickerModelToDateTime(this.startDate)
        const endDateTime = this.datePickerModelToDateTime(this.endDate)
        this.fetchAndRenderGraph({ startDateTime, endDateTime })
        if (endpoint === 'start') this.startMenu = false
        if (endpoint === 'end') this.endMenu = false
      },
      getAllowedStartDates (dateString) {
        const dateTime = DateTime.fromISO(dateString)
        // users should not be allowed to select a graph start time after the currently
        // selected event's start time
        const isBeforeEventStart = dateTime.diff(this.eventStartDateTime, 'days').toObject().days <= 0
        const isLessThanWeekFromEnd = this.datePickerModelToDateTime(this.endDate).diff(dateTime, 'days').toObject().days < 7
        return isBeforeEventStart && isLessThanWeekFromEnd
      },
      getAllowedEndDates (dateString) {
        const dateTime = DateTime.fromISO(dateString)
        // Users should not be allowed to select a graph end time before the currently
        // selected event’s end time
        const isAfterEventEnd = dateTime.diff(this.eventEndDateTime, 'days').toObject().days >= -1
        const isLessThanWeekFromStart = dateTime.diff(this.datePickerModelToDateTime(this.startDate), 'days').toObject().days < 7
        return isAfterEventEnd && isLessThanWeekFromStart
      },
      closeModal () {
        this.$emit('close-modal')
      },
    },
  }
</script>

<style lang="scss" scoped>
.event-detail-title {
  font-size: 25px;
  color: white;
  font-weight: bold;
  letter-spacing: 0.5px;
}
.event-detail-card {
  padding: 43px 61px 31px 53px !important;
}
.event-detail-header {
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}
.event-detail-label {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  &__icon {
    background-color: $primary;
    border-radius: 50px;
    height: 48px;
    width: 48px;
    display: flex;
    & > * {
      margin: auto;
    }
  }
  &__type {
    font-family: "Degular", sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 25px;
    letter-spacing: 0.5px;
    margin-left: 10px;
  }
}
.event-detail-description {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
}
.event-detail-table {
  margin-left: 0;
  max-width: 714px;
  &__header {
    height: 16px;
    font-size: 14px;
    padding: 0;
    color: #535353;
  }
  &__data {
    color: #535353;
    font-size: 18px;
  }
  &__detail {
    color: #535353;
    font-size: 14px;
  }
  & .col {
    flex: 0 1 290px;
  }
}
.event-detail-graph__controls {
  display:flex;
  flex-direction:row;
  flex-wrap:nowrap;
  justify-content:space-between;
  margin-top: 50px;
  &.v-btn {
    letter-spacing: 1px;
  }
}
.event-detail-pagination {
  height:73px;
  width:200px;
  margin:auto;
  display:flex;
  flex-direction:row;
}
.event-modal-close {
  position: absolute;
  top: 0;
  right: 0;
}
.legend-list {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: flex-start;
    margin: 15px 0;
    padding: 0;
    min-height:4rem;
    .zone-container {
      flex: 0 1 175px;
      min-height: 0;
    .zone-icon {
      color: white;
      height:2rem;
      width:2rem;
      padding:.35rem;
      border-radius: 5px;
      cursor:pointer;
    }
      .zone-name {
        font-weight:500;
      }
      .sensor-type {
        font-size:.75rem;
        color:#535353;
        font-weight:500;
      }
  }
    .graph-key-icon {
      background-color:#44AA99;
      color:white;
      height:2.2rem;
      width:2.2rem;
      padding:.5rem;
      font-size:1.3rem;
      line-height:1.3rem;
      border-radius:3px;
      cursor:pointer;
    }
    .v-list-item {
      padding-right:0;
      .v-list-item__icon {
        margin:auto;
      }
      .v-list-item__content {
        margin-left:.5rem;
      }
    }
  }
  .nrs-caption {
    font-family: "Degular", sans-serif;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 18px;
    color: #{$primary};
    text-decoration: none;
    /* identical to box height */
    display: flex;
    align-items: center;
    letter-spacing: 0.382511px;
  }
</style>
